import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  BookHeart, Image, Music, Film, FileText, Heart, Star,
  Upload, Plus, Calendar, Camera, Mic, PlayCircle, RefreshCw,
  MessageCircle, Share2, Download, ChevronRight, Flower
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Legacy prompt questions
const LEGACY_PROMPTS = [
  { id: 1, text: "What is a favorite memory from your childhood?", category: "memories" },
  { id: 2, text: "What music would you like to hear today?", category: "music" },
  { id: 3, text: "What was the happiest day of your life?", category: "memories" },
  { id: 4, text: "What advice would you give to your grandchildren?", category: "wisdom" },
  { id: 5, text: "Tell us about someone who changed your life.", category: "relationships" },
  { id: 6, text: "What are you most proud of?", category: "achievements" },
  { id: 7, text: "What was your favorite family tradition?", category: "traditions" },
  { id: 8, text: "If you could relive one day, which would it be?", category: "memories" }
];

const LegacyBereavementModule = () => {
  const [legacy, setLegacy] = useState(null);
  const [bereavementPlan, setBereavementPlan] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('legacy');
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  const fetchLegacyData = useCallback(async () => {
    try {
      // Would fetch patient's legacy data
      const res = await fetch(`${API}/api/phase38-44/legacy/library`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLegacy(data);
      }
    } catch (err) {
      console.error('Error fetching legacy:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLegacyData();
    // Select random prompt for daily engagement
    const today = new Date().getDate();
    setCurrentPrompt(LEGACY_PROMPTS[today % LEGACY_PROMPTS.length]);
  }, [fetchLegacyData]);

  // Upload legacy item (photo, audio, video, document)
  const uploadLegacyItem = async (file, type, description) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('description', description);

    try {
      setUploadProgress(10);
      const res = await fetch(`${API}/api/phase38-44/legacy/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      setUploadProgress(100);
      if (res.ok) {
        toast({ title: "Upload Complete", description: "Memory saved to Legacy Library." });
        fetchLegacyData();
      }
    } catch (err) {
      toast({ title: "Upload Failed", description: "Please try again", variant: "destructive" });
    } finally {
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  // Submit prompt response
  const submitPromptResponse = async (response) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/legacy/prompt-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt_id: currentPrompt.id,
          prompt_text: currentPrompt.text,
          response: response,
          category: currentPrompt.category
        })
      });

      if (res.ok) {
        toast({ 
          title: "Memory Saved", 
          description: "This will be added to the Legacy Vault and shared with the care team." 
        });
        // Move to next prompt
        const nextIndex = (LEGACY_PROMPTS.findIndex(p => p.id === currentPrompt.id) + 1) % LEGACY_PROMPTS.length;
        setCurrentPrompt(LEGACY_PROMPTS[nextIndex]);
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to save response", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 bg-gradient-to-br from-amber-50 via-white to-rose-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookHeart className="h-7 w-7 text-rose-500" />
            Legacy & Life Review
          </h1>
          <p className="text-gray-600">Celebrating the person behind the patient</p>
        </div>
        <Button className="bg-rose-500 hover:bg-rose-600">
          <Plus className="h-4 w-4 mr-2" />
          Add to Legacy Vault
        </Button>
      </div>

      {/* Daily Prompt Card */}
      <Card className="bg-gradient-to-r from-rose-100 to-amber-100 border-rose-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="bg-white p-3 rounded-full">
              <MessageCircle className="h-6 w-6 text-rose-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-rose-900 mb-2">Today's Reflection</h3>
              <p className="text-rose-800 text-lg italic">"{currentPrompt?.text}"</p>
              <div className="mt-4">
                <Textarea 
                  placeholder="Share your thoughts or memories..."
                  className="bg-white/80 border-rose-200"
                />
                <div className="flex justify-between items-center mt-3">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="border-rose-300">
                      <Mic className="h-4 w-4 mr-1" />
                      Record
                    </Button>
                    <Button variant="outline" size="sm" className="border-rose-300">
                      <Camera className="h-4 w-4 mr-1" />
                      Photo
                    </Button>
                  </div>
                  <Button 
                    className="bg-rose-500 hover:bg-rose-600"
                    onClick={() => {
                      const textarea = document.querySelector('textarea');
                      if (textarea?.value) {
                        submitPromptResponse(textarea.value);
                        textarea.value = '';
                      }
                    }}
                  >
                    Save Memory
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="legacy">Legacy Vault</TabsTrigger>
          <TabsTrigger value="timeline">Life Timeline</TabsTrigger>
          <TabsTrigger value="bereavement">Support</TabsTrigger>
        </TabsList>

        {/* Legacy Vault */}
        <TabsContent value="legacy" className="space-y-4">
          {/* Upload Progress */}
          {uploadProgress > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Upload className="h-5 w-5 text-teal-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Uploading...</p>
                    <Progress value={uploadProgress} className="h-2 mt-1" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Media Categories */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { type: 'photos', icon: Image, color: 'teal', label: 'Photos', count: legacy?.photos?.length || 0 },
              { type: 'audio', icon: Music, color: 'purple', label: 'Audio', count: legacy?.audio?.length || 0 },
              { type: 'videos', icon: Film, color: 'blue', label: 'Videos', count: legacy?.videos?.length || 0 },
              { type: 'stories', icon: FileText, color: 'amber', label: 'Stories', count: legacy?.stories?.length || 0 }
            ].map(({ type, icon: Icon, color, label, count }) => (
              <Card key={type} className="cursor-pointer hover:border-teal-300 transition-colors">
                <CardContent className="p-4 text-center">
                  <div className={`bg-${color}-100 p-3 rounded-full w-fit mx-auto mb-2`}
                       style={{ backgroundColor: 
                         color === 'teal' ? '#ccfbf1' :
                         color === 'purple' ? '#f3e8ff' :
                         color === 'blue' ? '#dbeafe' : '#fef3c7'
                       }}>
                    <Icon className={`h-6 w-6`} style={{ color:
                      color === 'teal' ? '#0d9488' :
                      color === 'purple' ? '#9333ea' :
                      color === 'blue' ? '#2563eb' : '#d97706'
                    }} />
                  </div>
                  <h4 className="font-medium">{label}</h4>
                  <p className="text-sm text-gray-500">{count} items</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recent Memories */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="h-5 w-5 text-rose-500" />
                Recent Memories
              </CardTitle>
              <CardDescription>Pinned to EHR for care team visibility</CardDescription>
            </CardHeader>
            <CardContent>
              {legacy?.recent_entries?.length > 0 ? (
                <div className="space-y-4">
                  {legacy.recent_entries.map((entry, i) => (
                    <div key={i} className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${
                          entry.type === 'photo' ? 'bg-teal-100' :
                          entry.type === 'audio' ? 'bg-purple-100' :
                          entry.type === 'video' ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                          {entry.type === 'photo' && <Image className="h-5 w-5 text-teal-600" />}
                          {entry.type === 'audio' && <Music className="h-5 w-5 text-purple-600" />}
                          {entry.type === 'video' && <Film className="h-5 w-5 text-blue-600" />}
                          {entry.type === 'story' && <FileText className="h-5 w-5 text-amber-600" />}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{entry.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{entry.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">{entry.category}</Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon">
                            <PlayCircle className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon">
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookHeart className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No memories saved yet</p>
                  <p className="text-sm text-gray-400">Start building the legacy vault</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Favorite Music */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Music className="h-5 w-5 text-purple-500" />
                Favorite Music
              </CardTitle>
              <CardDescription>Songs that bring comfort and joy</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {legacy?.favorite_music?.map((song, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <PlayCircle className="h-5 w-5 text-purple-600" />
                      <div>
                        <p className="font-medium">{song.title}</p>
                        <p className="text-sm text-gray-500">{song.artist}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-purple-600">{song.genre}</Badge>
                  </div>
                )) || (
                  <p className="text-center text-gray-500 py-4">No music preferences added</p>
                )}
                <Button variant="outline" className="w-full mt-2 border-purple-300 text-purple-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Song
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Life Timeline */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Life Milestones</CardTitle>
              <CardDescription>A visual journey through important moments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-rose-200" />

                {/* Timeline Items */}
                <div className="space-y-6">
                  {legacy?.milestones?.map((milestone, i) => (
                    <div key={i} className="relative flex gap-4 items-start">
                      <div className="z-10 bg-white">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          milestone.type === 'birth' ? 'bg-pink-100' :
                          milestone.type === 'marriage' ? 'bg-rose-100' :
                          milestone.type === 'career' ? 'bg-blue-100' :
                          milestone.type === 'family' ? 'bg-amber-100' : 'bg-gray-100'
                        }`}>
                          {milestone.type === 'birth' && <Star className="h-5 w-5 text-pink-500" />}
                          {milestone.type === 'marriage' && <Heart className="h-5 w-5 text-rose-500" />}
                          {milestone.type === 'career' && <Star className="h-5 w-5 text-blue-500" />}
                          {milestone.type === 'family' && <Heart className="h-5 w-5 text-amber-500" />}
                        </div>
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{milestone.title}</h4>
                          <Badge variant="outline">{milestone.year}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                        {milestone.photo && (
                          <div className="mt-2 w-32 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Image className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  )) || (
                    <div className="text-center py-8 pl-16">
                      <Calendar className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500">No milestones recorded</p>
                    </div>
                  )}

                  {/* Add Milestone */}
                  <div className="relative flex gap-4 items-start">
                    <div className="z-10 bg-white">
                      <div className="w-12 h-12 rounded-full border-2 border-dashed border-rose-300 flex items-center justify-center cursor-pointer hover:border-rose-500">
                        <Plus className="h-5 w-5 text-rose-400" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 pt-3">Add a milestone</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bereavement Support */}
        <TabsContent value="bereavement" className="space-y-4">
          <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <CardHeader>
              <CardTitle className="text-lg text-purple-900 flex items-center gap-2">
                <Flower className="h-5 w-5" />
                Bereavement Support Program
              </CardTitle>
              <CardDescription>Compassionate care continues beyond</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Support Timeline */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-1">13</div>
                    <p className="text-sm text-gray-600">Months of Support</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-1">6</div>
                    <p className="text-sm text-gray-600">Scheduled Check-ins</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-1">∞</div>
                    <p className="text-sm text-gray-600">Access to Resources</p>
                  </div>
                </div>

                {/* Automated Workflow */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Automated Support Workflow</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { week: 1, task: "Condolence call from Social Worker", status: "scheduled" },
                        { week: 2, task: "Grief resources mailed", status: "pending" },
                        { week: 4, task: "Support group invitation", status: "pending" },
                        { week: 8, task: "Follow-up wellness check", status: "pending" },
                        { week: 12, task: "Anniversary of care support", status: "pending" },
                        { week: 52, task: "One-year remembrance card", status: "pending" }
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                            item.status === 'completed' ? 'bg-green-100 text-green-700' :
                            item.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 
                            'bg-gray-100 text-gray-500'
                          }`}>
                            W{item.week}
                          </div>
                          <span className={`flex-1 ${
                            item.status === 'completed' ? 'line-through text-gray-400' : ''
                          }`}>
                            {item.task}
                          </span>
                          <Badge variant="outline" className={
                            item.status === 'completed' ? 'text-green-600' :
                            item.status === 'scheduled' ? 'text-blue-600' : 'text-gray-400'
                          }>
                            {item.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Resources */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-auto p-4 justify-start">
                    <FileText className="h-5 w-5 mr-3 text-purple-500" />
                    <div className="text-left">
                      <p className="font-medium">Grief Resources</p>
                      <p className="text-xs text-gray-500">Articles, videos, and guides</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-auto p-4 justify-start">
                    <MessageCircle className="h-5 w-5 mr-3 text-purple-500" />
                    <div className="text-left">
                      <p className="font-medium">Support Groups</p>
                      <p className="text-xs text-gray-500">Local and virtual options</p>
                    </div>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LegacyBereavementModule;
